import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';

const defaultTranslation = { SNO: 'S.No' };

const ReportPage = ({
  usre = [],
  reany = [],
  botusers = [],
  ratingli = [],
  faqs = [],
  prdata = [],
  opdata = [],
  translation = defaultTranslation,
  userRole = 'user', // 'operator' or other roles
}) => {
  const [category, setCategory] = useState('1');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  useEffect(() => {
    if (userRole === 'operator' && category === '1') {
      setCategory('2');
    }
  }, [userRole, category]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const filteredData = useMemo(() => {
    const filterByDate = (rows, dateField) =>
      rows.filter((row) => {
        const dateValue = row[dateField];
        if (!dateValue) return true;
        const ts = new Date(dateValue).getTime();
        const minTs = minDate ? new Date(minDate).getTime() : null;
        const maxTs = maxDate ? new Date(maxDate).getTime() : null;
        if (minTs && ts < minTs) return false;
        if (maxTs && ts > maxTs) return false;
        return true;
      });

    switch (category) {
      case '1':
        return filterByDate(usre, 'cdate');
      case '2':
        return filterByDate(reany, 'cdate');
      case '3':
        return filterByDate(botusers, 'cdate');
      case '4':
        return filterByDate(ratingli, 'cdate');
      case '5':
        return filterByDate(faqs, 'faq_cdate');
      case '6':
        return filterByDate(prdata, 'created');
      case '7':
        return filterByDate(opdata, 'l_created');
      default:
        return [];
    }
  }, [category, usre, reany, botusers, ratingli, faqs, prdata, opdata, minDate, maxDate]);

  const { columns, data } = useMemo(() => {
    const baseColumns = [{ id: 'sno', label: translation.SNO, render: (_, idx) => idx + 1 }];

    switch (category) {
      case '1':
      case '2':
        return {
          columns: [
            ...baseColumns,
            { id: 'ctype', label: 'Category', render: (row) => row.ctype },
            { id: 'name', label: 'User', render: (row) => row.name },
            { id: 'mobile', label: 'Mobile Number', render: (row) => row.mobile },
            { id: 'email', label: 'Email', render: (row) => (row.email === 0 ? 'No E-mail Available' : row.email) },
            { id: 'cdate', label: 'Date', render: (row) => formatDate(row.cdate) },
          ],
          data: filteredData,
        };
      case '3':
        return {
          columns: [
            ...baseColumns,
            { id: 'ctype', label: 'Category', render: (row) => row.ctype },
            { id: 'name', label: 'User/Bot Name', render: (row) => row.name },
            { id: 'mobile', label: 'Mobile Number', render: (row) => row.mobile },
            { id: 'status', label: 'Status', render: (row) => row.status },
            { id: 'cdate', label: 'Date', render: (row) => formatDate(row.cdate) },
          ],
          data: filteredData,
        };
      case '4':
        return {
          columns: [
            ...baseColumns,
            { id: 'ctype', label: 'Category', render: (row) => row.ctype },
            { id: 'name', label: 'User', render: (row) => row.name },
            { id: 'mobile', label: 'Mobile Number', render: (row) => row.mobile },
            { id: 'email', label: 'Email', render: (row) => (row.email === 0 ? 'No E-mail Available' : row.email) },
            { id: 'cdate', label: 'Date', render: (row) => formatDate(row.cdate) },
          ],
          data: filteredData,
        };
      case '5':
        return {
          columns: [
            ...baseColumns,
            { id: 'category', label: 'Category', render: (row) => row.category },
            { id: 'bots', label: 'Bot Name', render: (row) => row.bots },
            { id: 'faq_question', label: 'Question', render: (row) => row.faq_question },
            { id: 'faq_answer', label: 'Answer', render: (row) => row.faq_answer },
            { id: 'faq_cdate', label: 'Date', render: (row) => formatDate(row.faq_cdate) },
          ],
          data: filteredData,
        };
      case '6':
        return {
          columns: [
            ...baseColumns,
            { id: 'ctype', label: 'Category', render: (row) => row.ctype },
            { id: 'b_name', label: 'Name', render: (row) => row.b_name },
            { id: 'response', label: 'Response', render: (row) => row.response },
            { id: 'error', label: 'Status', render: (row) => row.error },
            { id: 'created', label: 'Started', render: (row) => formatDate(row.created) },
          ],
          data: filteredData,
        };
      case '7':
        return {
          columns: [
            ...baseColumns,
            { id: 'b_name', label: 'Name', render: (row) => row.b_name },
            { id: 'operator', label: 'Assigned', render: (row) => row.operator },
            { id: 'resolved', label: 'Resolved', render: (row) => row.resolved },
            { id: 'owner', label: 'Transferred', render: (row) => row.owner },
            { id: 'l_created', label: 'Date', render: (row) => formatDate(row.l_created) },
            { id: 'lr_review', label: 'User Feedback%', render: (row) => row.lr_review },
            { id: 'duration', label: 'Duration', render: (row) => row.duration },
          ],
          data: filteredData,
        };
      default:
        return { columns: baseColumns, data: [] };
    }
  }, [category, filteredData, translation.SNO]);

  const categoryOptions = [
    ...(userRole !== 'operator'
      ? [
          { value: '1', label: 'Usage Report' },
          { value: '3', label: 'Bots & Users' },
        ]
      : []),
    { value: '2', label: 'Reports And Analytics' },
    { value: '4', label: 'Customers Rating' },
    { value: '5', label: 'FAQ' },
    { value: '6', label: 'Performance Reports' },
    { value: '7', label: 'Operator Reports' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/">
          Back
        </Button>
        <Typography variant="h5">Custom Report</Typography>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {categoryOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Start date"
                type="date"
                fullWidth
                size="small"
                value={minDate}
                onChange={(e) => setMinDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="End date"
                type="date"
                fullWidth
                size="small"
                value={maxDate}
                onChange={(e) => setMaxDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {data.length === 0 ? (
            <Typography align="center" color="text.secondary">
              No records found for this category.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id}>{col.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, idx) => (
                    <TableRow key={idx} hover>
                      {columns.map((col) => (
                        <TableCell key={col.id}>
                          {col.id === 'sno' ? col.render(row, idx) : col.render(row, idx)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ReportPage;
